import { useEffect, type RefObject } from 'react';

export interface DismissOptions {
  /**
   * 이 선택자에 걸리는 요소를 누른 것은 '바깥 클릭'으로 보지 않는다.
   *
   * 여는 버튼을 가리킨다. 없으면 버튼을 다시 눌러도 닫히지 않는다 —
   * 바깥 클릭이 먼저 닫고 뒤이은 click이 다시 토글해서, 누를 때마다
   * 깜빡이기만 하고 영원히 열린 채로 남는다.
   */
  readonly toggleSelector?: string;
  /** Esc로도 닫을지. 기본은 닫는다. */
  readonly escape?: boolean;
}

/**
 * 바깥을 누르거나 Esc를 눌렀을 때 닫는다.
 *
 * 호버로 여닫는 UI는 터치에서 영영 닫히지 않는다 — `mouseleave`가 오지
 * 않기 때문이다. 열리는 것마다 이 훅을 붙여서 닫는 길을 보장한다.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  onClose: () => void,
  { toggleSelector, escape = true }: DismissOptions = {},
): void {
  useEffect(() => {
    if (!enabled) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target !== null) {
        if (ref.current?.contains(target) === true) return;
        if (toggleSelector !== undefined && target.closest(toggleSelector) !== null) return;
      }
      onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onClose();
    };

    // 캡처가 아니라 버블 단계에서 듣는다. 캡처로 받으면 패널 안의 버튼이
    // 눌리기 전에 닫혀서 클릭이 아무 일도 하지 않는다.
    window.addEventListener('pointerdown', onPointerDown);
    if (escape) window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, enabled, onClose, toggleSelector, escape]);
}
